'use client';

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loginWithProvider, requestOtp, verifyOtp } from '@/entities/customer/client';
import { ApiError } from '@/shared/api';
import { cx } from '@/shared/lib/cx';
import { formatPhone, isValidMobile, phoneDigits } from '@/shared/lib/phone';
import { isAppPath } from '@/shared/lib/route';
import { Checkbox } from '@/shared/ui/checkbox';
import { Dialog } from '@/shared/ui/dialog';
import { Icon } from '@/shared/ui/icon';
import { OtpInput, type OtpInputHandle } from '@/shared/ui/otp-input';
import { Spinner } from '@/shared/ui/spinner';

import { authPanelActions, type AuthPanelRootState } from '../model/auth-panel-slice';
import styles from './auth-panel.module.scss';

type Provider = 't-id' | 'yandex-id';
const EMPTY_CODE = ['', '', '', ''];
/**
 * Sign-in panel (design 08B). Closing keeps the typed phone, consent, screen and code.
 * Everything is mocked: no SMS is sent, see docs/MOCKING.md for demo codes.
 */
export function AuthPanel() {
  const panel = useSelector((state: AuthPanelRootState) => state.authPanel);
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Only same-origin paths are accepted as the return target (no open redirect).
  const loginValue = searchParams.get('login');
  const loginParam = isAppPath(loginValue) ? loginValue : null;
  const open = panel.open || loginParam !== null;
  const returnTo = panel.returnTo ?? loginParam;

  const [screen, setScreen] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const [consent, setConsent] = useState(false);
  const [consentHint, setConsentHint] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [providerBusy, setProviderBusy] = useState<Provider | null>(null);
  const [code, setCode] = useState<string[]>(EMPTY_CODE);
  const [codeError, setCodeError] = useState<'wrong' | 'expired' | null>(null);
  const [logging, setLogging] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [toast, setToast] = useState(false);
  const phoneRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<OtpInputHandle>(null);

  const ticking = screen === 'code' && resendAt !== null && codeError !== 'expired';
  useEffect(() => {
    if (!ticking) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [ticking]);

  const secondsLeft = resendAt && now ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;
  const phoneValid = isValidMobile(phone);
  const phoneInvalid = touched && phone.length > 0 && !phoneValid;
  const expired = codeError === 'expired';
  const codeComplete = code.every((digit) => digit.length === 1);

  const close = () => {
    setConsentHint(false);
    dispatch(authPanelActions.closed());
    if (loginParam !== null && isAppPath(pathname)) router.replace(pathname, { scroll: false });
  };

  const finish = () => {
    dispatch(authPanelActions.closed());
    setToast(true);
    setScreen('phone');
    setCode(EMPTY_CODE);
    setResendAt(null);
    if (isAppPath(returnTo) && returnTo !== pathname) router.push(returnTo);
    else if (loginParam !== null && isAppPath(pathname))
      router.replace(pathname, { scroll: false });
    router.refresh();
  };

  const startTimer = (seconds: number) => {
    const started = Date.now();
    setNow(started);
    setResendAt(started + seconds * 1000);
  };

  const sendCode = async () => {
    if (!phoneValid || sending) return;
    if (!consent) {
      setConsentHint(true);
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const result = await requestOtp(phone);
      setSentTo(formatPhone(phone));
      setScreen('code');
      setCode(EMPTY_CODE);
      setCodeError(null);
      startTimer(result.resendAfterSeconds);
      requestAnimationFrame(() => otpRef.current?.focus(0));
    } catch (error) {
      setSendError(
        error instanceof ApiError && error.code !== 'SMS_SEND_FAILED'
          ? error.message
          : 'Не удалось отправить код. Попробуйте еще раз.',
      );
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    setCode(EMPTY_CODE);
    setCodeError(null);
    try {
      const result = await requestOtp(phone);
      startTimer(result.resendAfterSeconds);
      requestAnimationFrame(() => otpRef.current?.focus(0));
    } catch (error) {
      setCodeError(null);
      setSendError(
        error instanceof ApiError ? error.message : 'Не удалось отправить код. Попробуйте еще раз.',
      );
      setScreen('phone');
    }
  };

  const login = async () => {
    if (!codeComplete || logging || expired) return;
    setLogging(true);
    try {
      await verifyOtp(phone, code.join(''));
      finish();
    } catch (error) {
      setCodeError(
        error instanceof ApiError && error.code === 'CODE_EXPIRED' ? 'expired' : 'wrong',
      );
    } finally {
      setLogging(false);
    }
  };

  const loginWith = async (provider: Provider) => {
    if (providerBusy) return;
    if (!consent) {
      setConsentHint(true);
      return;
    }
    setProviderBusy(provider);
    try {
      await loginWithProvider(provider);
      finish();
    } catch (error) {
      setSendError(
        error instanceof ApiError ? error.message : 'Не удалось войти. Попробуйте еще раз.',
      );
    } finally {
      setProviderBusy(null);
    }
  };

  const onPhoneChange = (raw: string) => {
    let digits = phoneDigits(raw);
    // Deleting a formatting character removes the digit before it.
    if (raw.length < formatPhone(phone).length && digits === phone) digits = digits.slice(0, -1);
    setPhone(digits);
    setTouched(false);
    setSendError(null);
  };

  // The arrow only returns to the phone step; "Изменить" also clears the error and focuses the number (08B).
  const back = () => setScreen('phone');
  const changePhone = () => {
    setScreen('phone');
    setCodeError(null);
    requestAnimationFrame(() => phoneRef.current?.focus());
  };

  const getCodeLabel = sendError ? 'Отправить код снова' : 'Получить код';
  const timerText = `Отправить код повторно через 00:${String(secondsLeft).padStart(2, '0')}`;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Вход в личный кабинет"
        variant="right"
        width="25rem"
        shadow="drawer-soft"
        className={styles.panel}
      >
        <div className={styles.bar}>
          {screen === 'code' ? (
            <button
              type="button"
              className={styles.icon}
              onClick={back}
              aria-label="Назад к номеру телефона"
            >
              <Icon name="arrow-back" size={22} strokeWidth={1.4} />
            </button>
          ) : (
            <span className={styles.spacer} />
          )}
          <button type="button" className={styles.icon} onClick={close} aria-label="Закрыть">
            <Icon name="close" size={20} strokeWidth={1.4} />
          </button>
        </div>

        {screen === 'phone' ? (
          <div className={styles.screen}>
            <h2 className={styles.heading}>Войти или зарегистрироваться</h2>
            <div className={styles.providers}>
              {(['t-id', 'yandex-id'] as const).map((provider) => {
                const busy = providerBusy === provider;
                const name = provider === 't-id' ? 'T-ID' : 'Яндекс ID';
                return (
                  <button
                    key={provider}
                    type="button"
                    className={styles.provider}
                    disabled={providerBusy !== null}
                    aria-busy={busy || undefined}
                    onClick={() => {
                      void loginWith(provider);
                    }}
                  >
                    {busy ? (
                      <>
                        <Spinner tone="dark" />
                        Переходим в {name}…
                      </>
                    ) : (
                      `Войти с ${name}`
                    )}
                  </button>
                );
              })}
            </div>
            <div className={styles.divider}>
              <span />
              или
              <span />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="auth-phone">
                Номер телефона
              </label>
              <input
                id="auth-phone"
                ref={phoneRef}
                className={cx(styles.input, phoneInvalid && styles.invalid)}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+7 999 999-99-99"
                value={formatPhone(phone)}
                onChange={(event) => onPhoneChange(event.target.value)}
                onBlur={() => setTouched(true)}
                aria-invalid={phoneInvalid || undefined}
                aria-describedby={phoneInvalid ? 'auth-phone-error' : undefined}
              />
              {phoneInvalid && (
                <span className={styles.error} role="alert" id="auth-phone-error">
                  Введите корректный номер телефона
                </span>
              )}
            </div>
            <Checkbox
              className={styles.consent}
              checked={consent}
              invalid={consentHint && !consent}
              onChange={(event) => {
                setConsent(event.target.checked);
                setConsentHint(false);
              }}
              label={
                <>
                  Продолжая, вы соглашаетесь с{' '}
                  <Link className={styles.link} href={'/info/terms'}>
                    Пользовательским соглашением
                  </Link>{' '}
                  и{' '}
                  <Link className={styles.link} href={'/info/privacy'}>
                    Политикой обработки персональных данных
                  </Link>
                  .
                </>
              }
            />
            {sendError && (
              <div className={styles.alert} role="alert">
                <Icon name="alert" size={15} />
                <span>{sendError}</span>
              </div>
            )}
            <button
              type="button"
              className={styles.primary}
              disabled={!phoneValid || sending}
              data-busy={sending || undefined}
              aria-busy={sending || undefined}
              onClick={() => {
                void sendCode();
              }}
            >
              {sending ? (
                <>
                  <Spinner />
                  Отправляем код…
                </>
              ) : (
                getCodeLabel
              )}
            </button>
          </div>
        ) : (
          <div className={styles.screen}>
            <h2 className={styles.heading}>Введите код</h2>
            <p className={styles.sent}>Отправили код на номер</p>
            <div className={styles.number}>
              <span>{sentTo}</span>
              <button type="button" className={styles.action} onClick={changePhone}>
                Изменить
              </button>
            </div>
            <OtpInput
              ref={otpRef}
              className={styles.otp}
              value={code}
              label="Код из SMS, четыре цифры"
              onChange={(next) => {
                setCode(next);
                if (codeError === 'wrong') setCodeError(null);
              }}
              onSubmit={() => {
                void login();
              }}
              invalid={codeError !== null}
              disabled={logging}
            />
            <div className={styles.messages}>
              {codeError === 'wrong' && (
                <span className={styles.error} role="alert">
                  Неверный код. Проверьте цифры и попробуйте еще раз.
                </span>
              )}
              {expired && (
                <span className={styles.error} role="alert">
                  Срок действия кода истек. Запросите новый.
                </span>
              )}
            </div>
            <button
              type="button"
              className={styles.primary}
              disabled={!codeComplete || logging || expired}
              data-busy={logging || undefined}
              aria-busy={logging || undefined}
              onClick={() => {
                void login();
              }}
            >
              {logging ? (
                <>
                  <Spinner />
                  Проверяем код…
                </>
              ) : (
                'Войти'
              )}
            </button>
            <div className={styles.resend}>
              {expired ? (
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() => {
                    void resend();
                  }}
                >
                  Отправить код повторно
                </button>
              ) : secondsLeft > 0 ? (
                <span className={styles.timer} aria-live="off">
                  {timerText}
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.action}
                  onClick={() => {
                    void resend();
                  }}
                >
                  Отправить код повторно
                </button>
              )}
            </div>
          </div>
        )}
      </Dialog>
      {toast && (
        <output className={styles.toast}>
          <Icon name="check" size={16} strokeWidth={1.8} className={styles.check} />
          <span>Вы вошли в аккаунт</span>
          <Link className={styles.action} href="/account" onClick={() => setToast(false)}>
            Открыть кабинет
          </Link>
          <button
            type="button"
            className={styles.dismiss}
            onClick={() => setToast(false)}
            aria-label="Скрыть уведомление"
          >
            <Icon name="close" size={14} />
          </button>
        </output>
      )}
    </>
  );
}
